/**
 * Générateur — Il modélise une situation de proportionnalité à l'aide d'une
 * fonction linéaire (versant FONCTIONS).
 * Attendu ID : 229 | Niveau : 3ème | Domaine : Fonctions (d13, O17)
 *
 * ANTI-DOUBLON : gen_218 (thème #6) porte le versant PROPORTIONNALITÉ des
 * mêmes attendus jumeaux (218/229 identiques en base) ; ici, les PROPRIÉTÉS
 * de la linéarité (séances B3S2), sans recouvrir les cartes de 218.
 *
 * Plan auto-validé 2026-07-16 (TODO §6) : « sanscoef » (homogénéité et
 * additivité SANS calculer le coefficient), « possible » (k(4) = 3 :
 * k(−8) = −5 possible ? QCM), « tableaux » (quel tableau correspond à une
 * fonction linéaire ? — le piège du tableau additif), « solde » (remise p % :
 * f(p) = coef·p, aller ET retour — jumeau fonctionnel de B3S3.Ex.4).
 */

import { randomChoice, randInt, tex, makeQcm, makeTexteTrou } from '../../../utils';
import { frNb, decFrag, NOMS_FN } from './fonctions';

const ATTENDU_ID = 229;
const L_SANSCOEF = 'Utiliser la linéarité sans le coefficient';
const L_POSSIBLE = 'Cette valeur est-elle possible pour une fonction linéaire ?';
const L_TABLEAUX = 'Quel tableau correspond à une fonction linéaire ?';
const L_SOLDE = 'La remise en pourcentage comme fonction linéaire';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ « sanscoef » — homogénéité/additivité ═════════

const genSansCoefTrous = () => {
    const nom = randomChoice(NOMS_FN);
    const a = randomChoice([3, 4, 5, 6, 7]);
    const fa10 = randomChoice([-36, -24, -18, 18, 24, 32, 36]);      // f(a) en dixièmes
    const k = randomChoice([2, 3, -1, -2]);
    const fA = fa10 / 10;
    const phrase = `$${nom}(${a * k}) = {{${frNb(fA * k)}}} \\qquad ${nom}(${a * 10}) = {{${frNb(fA * 10)}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${nom} est LINÉAIRE et ${tex(`${nom}(${a}) = ${decFrag(fA)}`)}.</p>`
        // La décomposition « 12 = 3 × 4 » ET la propriété « la linéarité
        // multiplie les images » donnaient tout le raisonnement.
        + `<p><b>Sans déterminer le coefficient, calcule ${tex(`${nom}(${a * k})`)} et `
        + `${tex(`${nom}(${a * 10})`)}.</b></p>`,
        { ...opts(L_SANSCOEF), mode: 'saisie' });
    payload._meta.params = { nom, a, fA, k };
    return payload;
};

// ═════════ « possible » — QCM justifié ═════════

const genPossibleQcm = () => {
    const nom = randomChoice(NOMS_FN);
    const a = randomChoice([2, 3, 4, 5]);
    const fA = randomChoice([-6, -3, 3, 5, 6, 7]);
    const k = randomChoice([-3, -2, 2, 3]);
    const attendu = k * fA;
    const possible = randInt(0, 1) === 1;
    const propose = possible ? attendu : attendu + randomChoice([-2, -1, 1, 2]);
    const chOui = `Oui : ${a * k} = ${k} × ${a}, donc ${nom}(${a * k}) = ${k} × ${fA} = ${attendu}.`;
    const chNon = `Non : ${a * k} = ${k} × ${a}, donc ${nom}(${a * k}) devrait valoir ${k} × ${fA} = ${attendu}.`;
    const chRien = 'On ne peut pas savoir sans connaître le coefficient.';
    const correct = possible ? chOui : chNon;
    const payload = makeQcm([chOui, chNon, chRien], correct,
        `<p>${nom} est LINÉAIRE et ${nom}(${a}) = ${fA}.</p>`
        + `<p><b>Est-il possible que ${nom}(${a * k}) = ${propose} ?</b></p>`,
        opts(L_POSSIBLE));
    payload._meta.params = { nom, a, fA, k, propose, possible };
    return payload;
};

// ═════════ « tableaux » — le piège du tableau additif (QCM) ═════════

const genTableauxQcm = () => {
    const base = randInt(2, 5);
    const lesXs = [base, base * 2, base * 3, base * 4];
    const coef = randomChoice([1.5, 2, 2.5, 3, 4]);
    const bon = lesXs.map(x => frNb(x * coef));
    const additif = lesXs.map(x => frNb(x + coef * base));             // +constante : le piège
    const casse = lesXs.map((x, i) => frNb(i === 3 ? x * coef + base : x * coef)); // 3 quotients égaux, pas le 4e
    const T = (ys) => `x : ${lesXs.join(' ; ')} — images : ${ys.join(' ; ')}`;
    const correct = `Tableau A (${T(bon)})`;
    const choix = [correct, `Tableau B (${T(additif)})`, `Tableau C (${T(casse)})`];
    const payload = makeQcm(choix, correct,
        '<p>Un seul de ces tableaux est un tableau de fonction LINÉAIRE.</p>'
        // « Vérifie TOUS les quotients : trois quotients égaux ne suffisent
        // pas » désamorçait le piège du tableau « casse » (3 quotients égaux).
        + '<p><b>Lequel ?</b></p>',
        opts(L_TABLEAUX));
    payload._meta.params = { lesXs, coef, bon, additif, casse };
    return payload;
};

// ═════════ « solde » — remise p % (jumeau fonctionnel) ═════════

const genSoldeTrous = () => {
    const nom = randomChoice(NOMS_FN);
    const p = randomChoice([10, 15, 20, 25, 30, 40, 50]);
    const coefCent = 100 - p;
    // Prix multiples de 20 → soldés EXACTS (au centime, souvent entiers).
    const prix1 = randomChoice([20, 28, 40, 44, 60, 80]);
    const solde1Cent = prix1 * coefCent;
    const fr = (cents) => (cents % 100 === 0 ? String(cents / 100)
        : (cents % 10 === 0 ? `${Math.floor(cents / 100)},${(cents % 100) / 10}`
            : `${Math.floor(cents / 100)},${String(cents % 100).padStart(2, '0')}`));
    // Retour : prix soldé donné → prix initial (construit depuis un entier).
    const initial2 = randomChoice([36, 48, 52, 64, 76]);
    const solde2Cent = initial2 * coefCent;
    const phrase = `$${nom}(p) = {{${frNb(coefCent / 100)}}}p$\n`
        + `$\\text{article à } ${prix1} \\text{ € : soldé } {{${fr(solde1Cent)}}} \\text{ €} \\qquad`
        + ` \\text{article soldé } ${fr(solde2Cent).replace(',', '{,}')} \\text{ € : prix initial } {{${initial2}}} \\text{ €}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un magasin applique une remise de ${p} % sur tout. La fonction ${nom} associe au prix de `
        + `départ p le prix soldé ${tex(`${nom}(p)`)}.</p>`
        // « (divise par le coefficient) » donnait l'opération du retour.
        + '<p><b>Complète le coefficient, le prix soldé, puis retrouve un prix initial.</b></p>',
        { ...opts(L_SOLDE), mode: 'saisie' });
    payload._meta.params = { nom, p, prix1, initial2 };
    return payload;
};

const GENERATORS = {
    sanscoef_texte_trou_saisie: genSansCoefTrous,
    possible_qcm_unique: genPossibleQcm,
    tableaux_qcm_unique: genTableauxQcm,
    solde_texte_trou_saisie: genSoldeTrous,
};
const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'sanscoef', label: L_SANSCOEF, formatsList: buildFormats('sanscoef') },
    { id: 'possible', label: L_POSSIBLE, formatsList: buildFormats('possible') },
    { id: 'tableaux', label: L_TABLEAUX, formatsList: buildFormats('tableaux') },
    { id: 'solde', label: L_SOLDE, formatsList: buildFormats('solde') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
